<template>
  <div class="main-wrapper">
    <div style="height: 5px"></div>

    <div class="message-header">
      <el-form label-position="left" inline class="table-expand">
        <el-form-item label="工单编号">
          <span>{{ ticket.ticketNo }}</span>
        </el-form-item>
        <el-form-item label="优先级">
          <span>{{ getTicketPriority() }}</span>
        </el-form-item>
        <el-form-item label="工单类型">
          {{ getTicketCategory() }}
          <span v-if="ticket.resourceId">{{ ticket.resourceId }}</span>
        </el-form-item>
        <el-form-item label="提交时间">
          <span>{{ ticket.createdDate }}</span>
        </el-form-item>
        <el-form-item label="工单状态">
          <el-tag :type="getTicketStatusType()">{{ getTicketStatus() }}</el-tag>
        </el-form-item>
        <el-form-item label="" v-if="ticket.status !== 'Closed'">
          <el-button type="danger" @click="btnCloseClick">关闭工单</el-button>
        </el-form-item>
      </el-form>
    </div>

    <message-list :isReply="false" :user="user" :messages="messages"></message-list>

    <div v-if="isInputArea()" class="message-footer">
      <el-form label-position="left" ref="form" :model="form" style="width: 100%; margin: 10px">
        <el-form-item>
          <el-upload
            :drag="true"
            :action="uploadUrl"
            :auto-upload="true"
            :headers="{ Authorization: 'Bearer ' + uploadToken }"
            :show-file-list="false"
            :before-upload="uploadBefore"
            :on-progress="uploadProgress"
            :on-success="uploadSuccess"
            :on-error="uploadError"
            :multiple="false"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">
              将文件拖到此处，或
              <em>点击上传</em>
            </div>
          </el-upload>
        </el-form-item>
        <el-form-item>
          <el-input
            type="textarea"
            :rows="2"
            :autosize="{ minRows: 4, maxRows: 10 }"
            placeholder="请输入内容，支持 Markdown 标签"
            v-model="form.content"
          ></el-input>
        </el-form-item>
        <el-form-item align="center">
          <el-button type="primary" @click="btnSubmitClick">提 交</el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ITicketMessage, ITicket, IUser } from '@/types/models';
import api, { ISubmitRequest, IUploadResult } from '@/api/my/ticketMessages';
import { PER_PAGE } from '@/const';
import { MessageType, TicketStatus } from '@/types/enums';
import utils from '@/utils';
import { auth } from '@/store';
import MessageList from '@/components/tickets/MessageList.vue';

interface IForm {
  content: string;
}

interface IState {
  ticket: ITicket;
  user: IUser;
  count: number;
  messages: ITicketMessage[];
  currentPage: number;
  offset: number;
  limit: number;
  uploadToken: string;
  uploadUrl: string;
  form: IForm;
}

export default defineComponent({
  name: 'TicketMessages',
  components: { MessageList },
  data() {
    return {
      ticket: {} as ITicket,
      user: {} as IUser,
      count: 0,
      messages: [],
      currentPage: 1,
      offset: 0,
      limit: PER_PAGE,
      uploadToken: auth.token,
      uploadUrl: api.getUploadUrl(),
      form: {
        content: '',
      },
    } as IState;
  },
  methods: {
    apiGet() {
      const ticketNo = this.$route?.params?.ticketNo as string;
      if (!ticketNo) return;

      api.get(
        {
          ticketNo,
        },
        (res) => {
          this.ticket = res.ticket;
          this.user = res.user;
          this.count = res.count;
          this.messages = res.messages;
        },
      );
    },

    apiClose() {
      api.close(
        {
          ticketNo: this.ticket.ticketNo,
        },
        (res) => {
          if (!res.value) return;
          utils.success('工单关闭成功！');
          this.ticket.status = TicketStatus.Closed;
        },
      );
    },

    apiSubmit(messageType: MessageType, storageKey: string, content: string) {
      const req: ISubmitRequest = {
        ticketNo: this.ticket.ticketNo,
        messageType,
        storageKey,
        content,
      };
      api.submit(req, (res) => {
        this.form.content = '';
        this.ticket.status = TicketStatus.Dealing;
        this.messages.push(res.message);
      });
    },

    getTicketPriority() {
      return utils.getTicketPriority(this.ticket.priority);
    },

    getTicketStatusType() {
      if (this.ticket.status === TicketStatus.Closed) {
        return 'success';
      }
      if (this.ticket.status === TicketStatus.Dealing) {
        return 'info';
      }
      return 'danger';
    },

    getTicketCategory() {
      return utils.getTicketCategory(this.ticket.category);
    },

    getTicketStatus() {
      return utils.getTicketStatus(this.ticket.status);
    },

    isInputArea() {
      return this.ticket.status !== TicketStatus.Closed;
    },

    btnCloseClick() {
      utils.alertDelete(
        {
          title: '关闭工单',
          text: '此操作将关闭工单，确定吗？',
          button: '确认关闭',
        },
        () => {
          this.apiClose();
        },
      );
    },

    btnSubmitClick() {
      if (this.form.content) {
        this.apiSubmit(MessageType.Text, '', this.form.content);
      }
    },

    uploadBefore(file: { size: number }) {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        utils.error('文件大小不能超过 10MB!');
      }
      return isLt10M;
    },

    uploadProgress() {
      utils.loading(true);
    },

    uploadSuccess(res: IUploadResult) {
      this.apiSubmit(res.messageType, res.storageKey, res.content);
    },

    uploadError(err: { message: string }) {
      utils.loading(false);
      const error = JSON.parse(err.message);
      utils.error(error.message);
    },
  },
  created() {
    this.$watch(
      () => this.$route.params,
      () => {
        this.apiGet();
      },
      // fetch the data when the view is created and the data is
      // already being observed
      { immediate: true },
    );
  },
});
</script>
<style scoped>
.message-header {
  padding: 10px 15px;
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  background-color: #f5f5f7;
}

.table-expand {
  font-size: 0;
  width: 100%;
  display: flex;
}
.table-expand label {
  width: 90px;
  color: #fff;
}
.table-expand .el-form-item {
  margin-right: 0;
  margin-bottom: 0;
  width: 25%;
}
.table-expand .el-form-item__label {
  color: #fff;
}

.message-footer {
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  background-color: #f5f5f7;
  padding-top: 20px;
  width: 100%;
  display: flex;
}
</style>
